import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';

/** ISO dates, clock times and date-times: a subtitle never breaks inside one of these at a hyphen or colon. */
const UNBREAKABLE_TOKEN = /(\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2})?Z?)?|\b\d{2}:\d{2}(?::\d{2})?Z?)/g;

function keepTokensWhole(text: string): ReactNode {
  const parts = text.split(UNBREAKABLE_TOKEN);
  if (parts.length === 1) return text;
  return parts.map((part, i) => (i % 2 === 1 ? <span key={i} className="whitespace-nowrap">{part}</span> : part));
}

export interface CardProps {
  /** Card title, 13px sans medium tracking-tight. Never mono. */
  title: string;
  /** Optional mono uppercase subtitle under the title (dates and times in it never wrap mid-token). */
  subtitle?: string;
  /** Grid span class string, e.g. "col-span-12 lg:col-span-6". */
  span?: string;
  /** Optional right-aligned header content (pivot strip, view-all link, count, legend). */
  rightSlot?: ReactNode;
  children?: ReactNode;
  className?: string;
  bodyClassName?: string;
  /** Removes body padding for tables and other edge-to-edge content. */
  flush?: boolean;
  /** Optional footer strip above a --hair divider (px-5 py-3). */
  footer?: ReactNode;
  id?: string;
  /** Heading level for the title. Default h3. */
  as?: 'h2' | 'h3' | 'h4';
}

/**
 * The universal panel (DESIGN_SYSTEM section 8.1).
 * border 1px --line, bg --panel, radius 2px; header px-5 py-3 with a --hair divider; body px-5 py-4.
 * The header keeps at least 8rem for the title block; when the right slot no longer fits beside
 * that (narrow cards, high zoom) it wraps under the title instead of squeezing the title to an
 * ellipsis and the subtitle to one word per line.
 */
export function Card({
  title,
  subtitle,
  span,
  rightSlot,
  children,
  className,
  bodyClassName,
  flush = false,
  footer,
  id,
  as: Heading = 'h3',
}: CardProps) {
  return (
    <section
      id={id}
      aria-label={title}
      className={cn('flex min-w-0 flex-col rounded-sm border border-line bg-panel', span, className)}
    >
      <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-hair px-5 py-3">
        <div className="min-w-0 grow basis-32">
          <Heading className="truncate text-nav font-medium tracking-tight text-ink">{title}</Heading>
          {subtitle ? (
            <p className="mt-0.5 font-mono text-2xs uppercase tracking-[0.15em] text-mute">{keepTokensWhole(subtitle)}</p>
          ) : null}
        </div>
        {rightSlot ? <div className="ml-auto flex shrink-0 items-center gap-3">{rightSlot}</div> : null}
      </header>
      <div className={cn('min-w-0 flex-1', flush ? '' : 'px-5 py-4', bodyClassName)}>{children}</div>
      {footer ? <footer className="border-t border-hair px-5 py-3">{footer}</footer> : null}
    </section>
  );
}
